import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { toast } from 'sonner';

// Stripe promise will be initialized dynamically
let stripePromise = null;

const PaymentForm = ({ clientSecret, onSuccess, onCancel, orderDetails }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [billingDetails, setBillingDetails] = useState({
    name: '',
    email: ''
  });

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);

    const card = elements.getElement(CardElement);

    try {
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: card,
          billing_details: {
            name: billingDetails.name || 'Restaurant Order Capacity Purchase',
            email: billingDetails.email || undefined,
          },
        },
      });

      if (error) {
        toast.error(`Payment failed: ${error.message}`);
        setProcessing(false);
      } else if (paymentIntent.status === 'succeeded') {
        toast.success('Payment successful!');
        onSuccess(paymentIntent.id);
      }
    } catch (err) {
      toast.error('Payment processing error');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
        <h3 className="font-medium text-blue-900 mb-2">Order Summary</h3>
        <div className="text-sm text-blue-800 space-y-1">
          <p><strong>Packages:</strong> {orderDetails.packages}</p>
          <p><strong>Order Capacity:</strong> {orderDetails.orders} orders</p>
          <p><strong>Total Amount:</strong> ${orderDetails.amount}</p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="billing-name" className="block text-sm font-medium text-gray-700 mb-2">
              Name (Optional)
            </label>
            <input
              type="text"
              id="billing-name"
              value={billingDetails.name}
              onChange={(e) => setBillingDetails({...billingDetails, name: e.target.value})}
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter your name"
            />
          </div>
          
          <div>
            <label htmlFor="billing-email" className="block text-sm font-medium text-gray-700 mb-2">
              Email (Optional)
            </label>
            <input
              type="email"
              id="billing-email"
              value={billingDetails.email}
              onChange={(e) => setBillingDetails({...billingDetails, email: e.target.value})}
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter your email"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Card Details
          </label>
          <div className="p-3 border border-gray-300 rounded-md bg-white">
            <CardElement
              options={{
                hidePostalCode: true,
                style: {
                  base: {
                    fontSize: '16px',
                    color: '#424770',
                    '::placeholder': {
                      color: '#aab7c4',
                    },
                  },
                  invalid: {
                    color: '#9e2146',
                  },
                },
              }}
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          disabled={processing}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || processing}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {processing ? 'Processing...' : `Pay $${orderDetails.amount}`}
        </button>
      </div>
    </form>
  );
};

const StripePaymentModal = ({ isOpen, onClose, clientSecret, orderDetails, onPaymentSuccess, apiClient }) => {
  const [stripeLoading, setStripeLoading] = useState(true);

  useEffect(() => {
    const initializeStripe = async () => {
      if (!stripePromise && isOpen) {
        try {
          // Fetch Stripe publishable key from backend
          const response = await apiClient.get('/stripe/public-key');
          const publishableKey = response.data.publishable_key;
          stripePromise = loadStripe(publishableKey);
          setStripeLoading(false);
        } catch (error) {
          console.error('Failed to load Stripe configuration:', error);
          toast.error('Payment system not configured. Please contact admin.');
          setStripeLoading(false);
        }
      } else if (stripePromise) {
        setStripeLoading(false);
      }
    };

    initializeStripe();
  }, [isOpen, apiClient]);

  const handlePaymentSuccess = async (paymentIntentId) => {
    try {
      // Call the backend to confirm payment and update capacity
      await onPaymentSuccess(paymentIntentId);
      onClose();
    } catch (error) {
      toast.error('Failed to confirm payment');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            Complete Payment
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {stripeLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Loading payment system...</span>
          </div>
        ) : stripePromise ? (
          <Elements stripe={stripePromise}>
            <PaymentForm
              clientSecret={clientSecret}
              onSuccess={handlePaymentSuccess}
              onCancel={onClose}
              orderDetails={orderDetails}
            />
          </Elements>
        ) : (
          <div className="text-center py-8">
            <p className="text-red-600">Payment system not configured. Please contact admin.</p>
            <button
              onClick={onClose}
              className="mt-4 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default StripePaymentModal;